import type { SubscriptionPlan } from '../../types';
import type {
  CheckoutFormState,
  CheckoutStepDefinition,
  LeadQualificationBusyRoutineBehavior,
  LeadQualificationConsultingExpectation,
  LeadQualificationFormState,
  LeadQualificationInjuryLimitation,
  LeadQualificationInvestmentInterest,
  LeadQualificationMainGoal,
  LeadQualificationStartTimeExpectation,
  LeadQualificationTrainingExperience,
  LeadQualificationTrainingFormat,
  LeadQualificationTrainingRelationship,
  LeadQualificationWeeklyCommitmentDays,
  PaymentMethod,
} from './checkout.types';

export const CHECKOUT_PROCESSING_ERROR_MESSAGE = 'Erro ao processar cadastro.';
export const CHECKOUT_STRIPE_PUBLIC_ERROR_MESSAGE = 'Não foi possível processar o pagamento. Tente novamente.';
export const CHECKOUT_STRIPE_PREPARING_MESSAGE = 'Quase lá. Estamos abrindo o pagamento, aguarde um instante';

export const CHECKOUT_STEPS: CheckoutStepDefinition[] = [
  { id: 'personal', label: 'Dados Pessoais' },
  { id: 'address', label: 'Endereço' },
  { id: 'payment', label: 'Pagamento' },
  { id: 'review', label: 'Revisão' },
];

export const CHECKOUT_PLAN_QUERY_PARAM = 'plan';

export const CHECKOUT_ACTION_QUERY_PARAM = 'action';

export const CHECKOUT_REF_QUERY_PARAM = 'ref';

/** Token one-time do checkout resumido (só pagar) para cliente Asaas existente. */
export const CHECKOUT_CHARGE_QUERY_PARAM = 'pay';

export const CHECKOUT_SUCCESS_DATA_QUERY_PARAM = 'data';

/** Path legado; preferir getCheckoutRoutePaths() conforme o host. */
export const CHECKOUT_AWAITING_PAYMENT_PATH = '/checkout/awaiting-payment';

export const CHECKOUT_SUCCESS_PATH = '/checkout/success';

export const CHECKOUT_WELCOME_THANK_YOU_MESSAGE =
  'É um prazer enorme ter você com a gente.';

export const CHECKOUT_WELCOME_NEXT_STEPS_MESSAGE =
  'Não se preocupe: em breve a minha equipe vai entrar em contato.';

export const CHECKOUT_WELCOME_SUPPORT_HINT_MESSAGE =
  'Se preferir, você também pode falar com o suporte clicando no botão acima.';

export const CHECKOUT_SUCCESS_EXPIRY_MS = 60 * 60 * 1000;

export const CHECKOUT_PIX_POLL_INTERVAL_MS = 2000;

export const CHECKOUT_PIX_REDIRECT_DELAY_MS = 2000;

export const CHECKOUT_MAX_WIDTH = 1200;

export const PAYMENT_METHOD_OPTIONS: Array<{ value: PaymentMethod; label: string; icon: string }> = [
  { value: 'credit_card', label: 'Cartão de crédito', icon: 'credit_card' },
  { value: 'pix', label: 'Pix', icon: 'qr_code_2' },
];

export const createMockCheckoutPlan = (planId: number): SubscriptionPlan => ({
  id: planId,
  name: planId === 1 ? 'Plano Mensal' : `Consultoria #${planId}`,
  price_cents: planId === 1 ? 29700 : 29700 + (planId - 1) * 5000,
  billing_cycle: 'monthly',
  training_cycle: 'monthly',
  loyalty_months: 0,
  auto_renew: true,
  is_single_payment: false,
  description: 'Acompanhamento online com treinos personalizados e suporte da consultoria.',
  benefits: 'Treinos personalizados\nSuporte via app\nAjustes periódicos',
  is_active: true,
  is_visible_in_app: true,
  created_at: new Date().toISOString(),
});

export const createInitialCheckoutForm = (): CheckoutFormState => ({
  personal: {
    fullName: '',
    email: '',
    cpf: '',
    callingCode: '55',
    phone: '',
    birthDate: '',
  },
  address: {
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    country: 'BR',
  },
  paymentMethod: null,
  card: {
    holderName: '',
    number: '',
    expiry: '',
    cvv: '',
  },
  payer: {
    name: '',
    email: '',
    cpf: '',
    postalCode: '',
    addressNumber: '',
    addressComplement: '',
    callingCode: '55',
    phone: '',
  },
  useStudentDataAsPayer: false,
  discountCents: 0,
});

export const createInitialLeadQualificationForm = (): LeadQualificationFormState => ({
  personal: {
    fullName: '',
    email: '',
    cpf: '',
    callingCode: '55',
    phone: '',
    birthDate: '',
  },
  investmentInterest: null,
  startTimeExpectation: null,
  mainGoal: null,
  trainingRelationship: null,
  trainingExperience: null,
  busyRoutineBehavior: null,
  weeklyCommitmentDays: null,
  trainingFormat: null,
  consultingExpectation: null,
  injuryLimitation: null,
});

export const LEAD_QUALIFICATION_INVESTMENT_INTEREST_QUESTION =
  'O valor da Consultoria é de R$ 297,00 / mês. Esse valor faz sentido pra você?';

export const LEAD_QUALIFICATION_START_TIME_QUESTION = 'Quando você pretende começar?';

export const LEAD_QUALIFICATION_MAIN_GOAL_QUESTION = 'Qual é o seu principal objetivo hoje?';

export const LEAD_QUALIFICATION_TRAINING_RELATIONSHIP_QUESTION =
  'Qual dessas frases representa melhor sua relação com o treino hoje?';

export const LEAD_QUALIFICATION_TRAINING_EXPERIENCE_QUESTION = 'Há quanto tempo você treina?';

export const LEAD_QUALIFICATION_BUSY_ROUTINE_QUESTION =
  'Quando sua rotina aperta, normalmente o que acontece?';

export const LEAD_QUALIFICATION_WEEKLY_COMMITMENT_QUESTION =
  'Quantos dias por semana você consegue se comprometer com os treinos hoje?';

export const LEAD_QUALIFICATION_TRAINING_FORMAT_QUESTION = 'Como você costuma treinar?';

export const LEAD_QUALIFICATION_CONSULTING_EXPECTATION_QUESTION =
  'O que você mais espera de uma consultoria?';

export const LEAD_QUALIFICATION_INJURY_LIMITATION_QUESTION =
  'Você possui alguma lesão ou limitação que precise ser considerada?';

export const LEAD_QUALIFICATION_INVESTMENT_INTEREST_OPTIONS: Array<{
  value: LeadQualificationInvestmentInterest;
  label: string;
}> = [
  { value: 'yes', label: 'Sim' },
  { value: 'maybe', label: 'Depende' },
  { value: 'no', label: 'Não' },
];

export const LEAD_QUALIFICATION_START_TIME_OPTIONS: Array<{
  value: LeadQualificationStartTimeExpectation;
  label: string;
}> = [
  { value: 'today', label: 'Hoje' },
  { value: 'this_week', label: 'Esta semana' },
  { value: 'this_month', label: 'Este mês' },
  { value: 'just_browsing', label: 'Estou apenas conhecendo' },
];

export const LEAD_QUALIFICATION_MAIN_GOAL_OPTIONS: Array<{
  value: Exclude<LeadQualificationMainGoal, 'health_quality_of_life'>;
  label: string;
}> = [
  { value: 'lose_weight', label: 'Emagrecer' },
  { value: 'gain_muscle', label: 'Ganhar massa muscular' },
  { value: 'body_definition', label: 'Definir o corpo' },
  { value: 'improve_pain', label: 'Melhorar dores' },
  { value: 'improve_posture', label: 'Melhorar postura' },
  { value: 'strengthen_core', label: 'Fortalecer o core' },
  { value: 'healthy_aging', label: 'Envelhecer com mais saúde' },
];

export const LEAD_QUALIFICATION_TRAINING_RELATIONSHIP_OPTIONS: Array<{
  value: LeadQualificationTrainingRelationship;
  label: string;
}> = [
  { value: 'trains_without_motivation', label: 'Treino mesmo quando não estou motivada.' },
  { value: 'returns_after_phases', label: 'Tenho fases melhores e piores, mas sempre volto.' },
  { value: 'needs_companion', label: 'Consigo treinar quando tenho alguém me acompanhando.' },
  { value: 'hard_to_keep_routine', label: 'Tenho muita dificuldade para manter uma rotina.' },
  { value: 'not_included_yet', label: 'Ainda não consegui incluir o treino na minha vida.' },
];

export const LEAD_QUALIFICATION_TRAINING_EXPERIENCE_OPTIONS: Array<{
  value: LeadQualificationTrainingExperience;
  label: string;
}> = [
  { value: 'over_2_years', label: 'Mais de 2 anos com frequência.' },
  { value: 'over_1_year_with_pauses', label: 'Mais de 1 ano, mas com algumas pausas.' },
  { value: 'six_to_12_months', label: 'Entre 6 meses e 1 ano.' },
  { value: 'under_6_months', label: 'Menos de 6 meses.' },
  { value: 'trained_but_stopped', label: 'Já treinei, mas estou parada.' },
  { value: 'never_trained', label: 'Nunca treinei.' },
];

export const LEAD_QUALIFICATION_BUSY_ROUTINE_OPTIONS: Array<{
  value: LeadQualificationBusyRoutineBehavior;
  label: string;
}> = [
  { value: 'adjusts_and_continues', label: 'Ajusto meus treinos e continuo.' },
  { value: 'pauses_then_returns', label: 'Fico alguns dias sem treinar, mas volto.' },
  { value: 'stops_hard_to_return', label: 'Normalmente paro e tenho dificuldade para voltar.' },
  { value: 'only_with_accountability', label: 'Só continuo quando alguém me cobra.' },
];

export const LEAD_QUALIFICATION_WEEKLY_COMMITMENT_OPTIONS: Array<{
  value: LeadQualificationWeeklyCommitmentDays;
  label: string;
}> = [
  { value: '5', label: '5 dias' },
  { value: '4', label: '4 dias' },
  { value: '3', label: '3 dias' },
  { value: '2', label: '2 dias' },
  { value: 'unknown', label: 'Ainda não sei' },
];

export const LEAD_QUALIFICATION_TRAINING_FORMAT_OPTIONS: Array<{
  value: LeadQualificationTrainingFormat;
  label: string;
}> = [
  { value: 'alone', label: 'Sozinha' },
  { value: 'with_personal', label: 'Com personal' },
  { value: 'mixed', label: 'Faço um pouco dos dois' },
  { value: 'currently_stopped', label: 'Atualmente estou parada' },
];

export const LEAD_QUALIFICATION_CONSULTING_EXPECTATION_OPTIONS: Array<{
  value: LeadQualificationConsultingExpectation;
  label: string;
}> = [
  { value: 'individualized_training', label: 'Um treino com ajustes individualizados' },
  { value: 'form_correction', label: 'Correção da minha execução.' },
  { value: 'goal_strategy', label: 'Estratégia para atingir meus objetivos.' },
  { value: 'routine_organization', label: 'Organização do treino dentro da minha rotina.' },
  { value: 'accountability', label: 'Alguém para me cobrar.' },
  { value: 'motivation', label: 'Motivação para treinar.' },
];

export const LEAD_QUALIFICATION_INJURY_LIMITATION_OPTIONS: Array<{
  value: LeadQualificationInjuryLimitation;
  label: string;
}> = [
  { value: 'none', label: 'Não.' },
  { value: 'yes_can_train', label: 'Sim, mas consigo treinar normalmente.' },
  { value: 'yes_needs_adaptations', label: 'Sim, preciso de adaptações.' },
];
